import { useEffect, useRef, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { useBreakpoint } from '../../hooks/useMediaQuery.ts';
import { EmptyState } from '../EmptyState/EmptyState.tsx';
import { Skeleton } from '../Skeleton/Skeleton.tsx';
import styles from './DataTable.module.css';
import { HeaderCell } from './HeaderCell.tsx';
import { PaginationBar } from './PaginationBar.tsx';
import { RowCard } from './RowCard.tsx';
import { SelectAllHeader, SelectCell } from './RowSelect.tsx';
import { useVirtualRows, VIRTUAL_FROM_ROWS } from './useVirtualRows.ts';
import {
  holdsControls,
  type Column,
  type Pagination,
  type RowSelection,
  type TableLabels,
  type TableSort,
} from './table-types.ts';

export type {
  Column,
  Pagination,
  RowSelection,
  SortDirection,
  TableLabels,
  TableSort,
} from './table-types.ts';

export type DataTableProps<Row> = {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  loading?: boolean;
  pagination?: Pagination | undefined;
  onRowClick?: ((row: Row) => void) | undefined;
  emptyTitle: string;
  emptyDescription?: string | undefined;
  labels: TableLabels;
  responsive?: 'cards' | 'scroll';
  selection?: RowSelection<Row> | undefined;
  activeKey?: string | null | undefined;
  highlightKey?: string | null | undefined;
  sort?: TableSort | undefined;
  toolbar?: ReactNode;
  footer?: ReactNode;
  bare?: boolean;
};

const SKELETON_ROWS = 5;
const HEADER_ROWS = 1;

const SpacerRow = ({ height }: { height: number }) =>
  height > 0 ? (
    <tr aria-hidden="true" className={styles['spacer']} style={{ height: `${String(height)}px` }} />
  ) : null;

const cellClassOf = <Row,>(column: Column<Row>): string | undefined => {
  if (column.action) return styles['action'];
  return column.numeric ? styles['numeric'] : undefined;
};

const footerRowOf = (footer: ReactNode) =>
  footer === undefined ? null : <div className={styles['footer']}>{footer}</div>;

const isWithinRow = (event: MouseEvent<HTMLTableRowElement>): boolean =>
  event.target instanceof Node && event.currentTarget.contains(event.target);

const stopRowEvent = (event: { stopPropagation: () => void }): void => {
  event.stopPropagation();
};

export const DataTable = <Row,>({
  columns,
  rows,
  rowKey,
  loading = false,
  pagination,
  onRowClick,
  emptyTitle,
  emptyDescription,
  labels,
  responsive = 'cards',
  selection,
  activeKey = null,
  highlightKey = null,
  sort,
  toolbar,
  footer,
  bare = false,
}: DataTableProps<Row>) => {
  const isTablet = useBreakpoint('md');
  const useCards = responsive === 'cards' && !isTablet;
  const tableRef = useRef<HTMLTableElement>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const focusedKey = useRef<string | null>(null);
  const virtual = useVirtualRows({
    count: rows.length,
    bodyRef,
    enabled: !loading && !useCards && rows.length > VIRTUAL_FROM_ROWS,
  });

  useEffect(() => {
    const target = activeKey === null ? null : `${activeKey}:${String(useCards)}`;
    if (activeKey === null || focusedKey.current === target) {
      focusedKey.current = target;
      return;
    }
    const row = tableRef.current?.querySelector<HTMLElement>(`tr[data-row-key="${CSS.escape(activeKey)}"]`);
    if (row) {
      row.focus();
      focusedKey.current = target;
      return;
    }
    virtual.scrollTo(rows.findIndex((candidate) => rowKey(candidate) === activeKey));
  }, [activeKey, useCards, virtual, rows, rowKey]);

  const pager = pagination ? <PaginationBar pagination={pagination} labels={labels} /> : footerRowOf(footer);
  const toolbarRow = toolbar === undefined ? null : <div className={styles['toolbar']}>{toolbar}</div>;
  const empty = <EmptyState title={emptyTitle} description={emptyDescription} />;
  const frameClass = [styles['frame'], bare ? styles['bare'] : ''].join(' ');

  const isEmpty = !loading && rows.length === 0;
  if (isEmpty && toolbarRow === null) return empty;

  const openRow = (row: Row) =>
    onRowClick === undefined
      ? undefined
      : () => {
          onRowClick(row);
        };

  const selectRow = (row: Row) =>
    onRowClick === undefined
      ? undefined
      : (event: MouseEvent<HTMLTableRowElement>) => {
          if (!isWithinRow(event)) return;
          onRowClick(row);
        };

  const openOnKey = (row: Row) =>
    onRowClick === undefined
      ? undefined
      : (event: KeyboardEvent<HTMLTableRowElement>) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          if (event.target !== event.currentTarget) return;
          event.preventDefault();
          onRowClick(row);
        };

  const body = loading
    ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <tr key={`skeleton-${String(index)}`}>
          {selection === undefined ? null : <td className={styles['select']} />}
          {columns.map((column) => (
            <td key={column.id}>
              <Skeleton />
            </td>
          ))}
        </tr>
      ))
    : rows.slice(virtual.first, virtual.last).map((row, offset) => (
        <tr
          key={rowKey(row)}
          ref={virtual.measureRow}
          data-index={virtual.first + offset}
          aria-rowindex={virtual.enabled ? virtual.first + offset + HEADER_ROWS + 1 : undefined}
          data-row-key={rowKey(row)}
          aria-current={rowKey(row) === activeKey ? 'true' : undefined}
          className={[
            styles['row'],
            onRowClick ? styles['clickable'] : '',
            rowKey(row) === activeKey ? styles['active'] : '',
            rowKey(row) === highlightKey ? styles['highlighted'] : '',
            selection?.selected.has(rowKey(row)) ? styles['selected'] : '',
          ].join(' ')}
          onClick={selectRow(row)}
          onKeyDown={openOnKey(row)}
          tabIndex={onRowClick ? 0 : undefined}
        >
          {selection === undefined ? null : <SelectCell row={row} rowKey={rowKey} selection={selection} />}
          {columns.map((column) => (
            <td
              key={column.id}
              className={cellClassOf(column)}
              onClick={holdsControls(column) ? stopRowEvent : undefined}
              onKeyDown={holdsControls(column) ? stopRowEvent : undefined}
            >
              {column.cell(row)}
            </td>
          ))}
        </tr>
      ));

  const cards = rows.map((row) => (
    <RowCard
      key={rowKey(row)}
      row={row}
      columns={columns}
      highlighted={rowKey(row) === highlightKey}
      onSelect={openRow(row)}
    />
  ));

  const table = (
    <div className={styles['scroller']}>
      <table
        ref={tableRef}
        className={styles['table']}
        role={onRowClick ? 'grid' : undefined}
        aria-rowcount={virtual.enabled ? rows.length + HEADER_ROWS : undefined}
      >
        <thead>
          <tr>
            {selection === undefined ? null : (
              <SelectAllHeader keys={rows.map(rowKey)} selection={selection} />
            )}
            {columns.map((column) => (
              <HeaderCell key={column.id} column={column} sort={sort} />
            ))}
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          <SpacerRow height={virtual.paddingTop} />
          {body}
          <SpacerRow height={virtual.paddingBottom} />
        </tbody>
      </table>
    </div>
  );
  const cardList = <div className={styles['cards']}>{loading ? <Skeleton height="120px" /> : cards}</div>;

  return (
    <div className={styles['wrapper']}>
      <div className={useCards && !isEmpty ? styles['cardsLayout'] : frameClass}>
        {toolbarRow}
        {isEmpty ? empty : useCards ? cardList : table}
        {isEmpty ? null : pager}
      </div>
    </div>
  );
};
